import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { X, Eye, EyeOff, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { addUser } from '../utils/userStore';

const RegisterPopup = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [mounted, setMounted] = useState(false);
  const openTimeRef = useRef(null);
  const backdropRef = useRef(null);
  const popupRef = useRef(null);
  const isClosingRef = useRef(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    dueDate: '',
    currentWeek: ''
  });

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Track when popup opens to prevent immediate closing
  useEffect(() => {
    if (isOpen) {
      // Reset closing flag
      isClosingRef.current = false;
      // Set open time immediately
      openTimeRef.current = Date.now();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      openTimeRef.current = null;
      isClosingRef.current = false;
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleRegister = (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      toast.error('Vui lòng điền đầy đủ thông tin bắt buộc!');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Mật khẩu phải có ít nhất 8 ký tự!');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Mật khẩu xác nhận không khớp!');
      return;
    }

    // Create user in mock store with validation
    try {
      const password = formData.password;
      if (password.length < 8) {
        toast.error('Mật khẩu phải có ít nhất 8 ký tự!');
        return;
      }
      addUser({
        email: formData.email,
        password: formData.password,
        dueDate: formData.dueDate,
        currentWeek: formData.currentWeek,
      });
    } catch (err) {
      toast.error(err.message || 'Email đã tồn tại');
      return;
    }

    toast.success('Đăng ký thành công! Vui lòng đăng nhập để tiếp tục.');
    navigate('/login');
    onClose();
  };

  // Disabled backdrop auto-close to prevent accidental immediate closing
  const handleBackdropMouseDown = undefined;

  const handleClose = useCallback(() => {
    isClosingRef.current = true;
    onClose();
    // After closing, return to home page
    try {
      navigate('/');
    } catch (_e) {
      // ignore navigation errors
    }
  }, [onClose]);

  // Don't render if not open or not mounted
  if (!isOpen) {
    return null;
  }
  
  if (!mounted) {
    return null;
  }

  const popupContent = (
    <div 
      className="fixed inset-0 z-[100]"
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'auto'
      }}
    >
      {/* Backdrop */}
      <div 
        ref={backdropRef}
        className="absolute inset-0 bg-black/50 flex items-center justify-center p-4"
        style={{ pointerEvents: 'auto' }}
      >
        {/* Popup */}
        <div 
          ref={popupRef}
          className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto relative z-[101]"
          onClick={(e) => {
            // Stop all propagation to prevent backdrop click
            e.stopPropagation();
            if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') {
              e.nativeEvent.stopImmediatePropagation();
            }
          }}
          onMouseDown={(e) => {
            // Prevent mousedown from reaching backdrop
            e.stopPropagation();
            if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') {
              e.nativeEvent.stopImmediatePropagation();
            }
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="register-popup-title"
        >
          {/* Header with illustration */}
          <div className="relative bg-gradient-to-r from-pink-500 to-pink-600 text-white rounded-t-lg overflow-hidden">
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors z-10"
              aria-label="Đóng"
            >
              <X className="w-5 h-5 text-white" />
            </button>

            {/* Illustration */}
            <div className="px-6 pt-8 pb-4">
              <div className="flex items-center justify-center space-x-2 mb-4">
                {/* Simple illustration with circles and lines representing pregnant women */}
                <div className="flex items-end space-x-1">
                  <div className="w-12 h-16 bg-white/30 rounded-full flex items-end justify-center pb-2">
                    <div className="w-8 h-12 bg-white/50 rounded-full"></div>
                  </div>
                  <div className="w-12 h-20 bg-white/30 rounded-full flex items-end justify-center pb-2">
                    <div className="w-8 h-16 bg-white/50 rounded-full"></div>
                  </div>
                  <div className="w-12 h-24 bg-white/30 rounded-full flex items-end justify-center pb-2">
                    <div className="w-8 h-20 bg-white/50 rounded-full"></div>
                  </div>
                  <div className="w-12 h-20 bg-white/30 rounded-full flex items-center justify-center">
                    <div className="w-6 h-6 bg-white/50 rounded-full"></div>
                  </div>
                </div>
              </div>
              <p className="text-center text-sm text-white/90 mb-2">
                Đã có tài khoản?{' '}
                <button 
                  onClick={() => {
                    handleClose();
                    navigate('/login');
                  }}
                  className="underline font-semibold hover:text-white"
                >
                  Đăng nhập
                </button>
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 bg-gradient-to-br from-pink-50/30 to-white">
            <h2 id="register-popup-title" className="text-2xl font-bold text-gray-800 mb-2">
              Theo dõi sự phát triển của bé
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Nhận hướng dẫn chuyên môn từ tài nguyên số 1 về thai kỳ và nuôi dạy con cái, được gửi qua email, ứng dụng và trang web của chúng tôi.
            </p>

            <form onSubmit={handleRegister} className="space-y-4">
              {/* Email */}
              <div>
                <Label htmlFor="popup-email" className="text-gray-700 text-sm">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="popup-email"
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 border-pink-200 focus:border-pink-400 bg-white"
                  required
                />
              </div>

              {/* Password */}
              <div>
                <Label htmlFor="popup-password" className="text-gray-700 text-sm">
                  Mật khẩu <span className="text-red-500">*</span>
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="popup-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Mật khẩu"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="border-pink-200 focus:border-pink-400 bg-white pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Mật khẩu phải từ 8-20 ký tự và chứa ít nhất 1 chữ cái viết hoa, 1 chữ cái viết thường, 1 số và 1 ký hiệu (ví dụ: !@-#_$%^&*).
                </p>
              </div>

              {/* Confirm Password */}
              <div>
                <Label htmlFor="popup-confirm-password" className="text-gray-700 text-sm">
                  Xác nhận lại mật khẩu <span className="text-red-500">*</span>
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="popup-confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Xác nhận lại mật khẩu"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="border-pink-200 focus:border-pink-400 bg-white pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Due Date or Child's Birthday */}
              <div>
                <Label htmlFor="popup-due-date" className="text-gray-700 text-sm">
                  Ngày dự sinh hoặc ngày sinh của bé
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="popup-due-date"
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    className="border-pink-200 focus:border-pink-400 bg-white"
                  />
                  <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Current Week */}
              <div>
                <Label htmlFor="popup-current-week" className="text-gray-700 text-sm">
                  Tuần thai
                </Label>
                <Input
                  id="popup-current-week"
                  type="number"
                  placeholder="VD: 12"
                  min="1"
                  max="40"
                  value={formData.currentWeek}
                  onChange={(e) => setFormData({ ...formData, currentWeek: e.target.value })}
                  className="mt-1 border-pink-200 focus:border-pink-400 bg-white"
                />
              </div>

              {/* Terms and Conditions */}
              <div className="flex items-start space-x-2 pt-2">
                <input 
                  type="checkbox" 
                  id="popup-terms"
                  className="w-4 h-4 text-pink-600 mt-1" 
                  required 
                />
                <label htmlFor="popup-terms" className="text-xs text-gray-600">
                  Tôi đồng ý với{' '}
                  <a href="#" className="text-pink-600 hover:underline font-medium">
                    Điều khoản sử dụng
                  </a>{' '}
                  và{' '}
                  <a href="#" className="text-pink-600 hover:underline font-medium">
                    Chính sách bảo mật
                  </a>
                </label>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white py-6 text-lg mt-4"
              >
                Đăng ký
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );

  // Render using portal to avoid z-index and event bubbling issues
  return createPortal(popupContent, document.body);
};

export default RegisterPopup;

